import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import ReceiptLineDialog from '../components/pettyCash/ReceiptLineDialog';
import LinesTable, { ScreenLine } from '../components/pettyCash/LinesTable';
import ApprovalPath from '../components/pettyCash/ApprovalPath';
import AccountingView, { CheckList } from '../components/pettyCash/AccountingView';
import FinanceLineDialog from '../components/pettyCash/FinanceLineDialog';
import LineImportDialog, { downloadReceiptTemplate } from '../components/pettyCash/LineImportDialog';
import ImprestCard, { ImprestText } from '../components/pettyCash/ImprestCard';
import { ReimbursementCard, ReimbursementDialog } from '../components/pettyCash/ReimbursementCard';
import { OriginalsCard, OriginalsDialog } from '../components/pettyCash/OriginalsCard';
import { ClaimStatusChip, WAITING_STATUSES, WaitingChip } from '../components/pettyCash/ClaimChips';
import SupplierQuickCreateDialog from '../components/suppliers/SupplierQuickCreateDialog';
import pettyCashApi, { ReimburseInput } from '../services/pettyCashApi';
import supplierApi from '../services/supplierApi';
import type { SupplierCreator } from '../types/suppliers';
import { showSnackbar } from '../store/slices/uiSlice';
import type {
  AccountingPreview,
  AllocationType,
  ClaimAttachment,
  ClaimDetail,
  ClaimLineInput,
  ClaimSaveInput,
  CodeName,
  Dims,
  FinanceLineChange,
  ImprestInput,
  LineType,
  PettyCashConfig,
  RoutePreview,
} from '../types/pettyCash';
import {
  amountInput,
  cleanDateInput,
  financeStageOf,
  formatAmount,
  formatDate,
  formatDateTime,
  paidTotal,
  parseOptionalAmount,
  processSettings,
  statusLook,
  todayIso,
} from '../utils/pettyCash';
import { saveBlob } from '../utils/formattedSoa';

/**
 * One petty cash claim (/petty-cash/new, /petty-cash/:id): the holder prepares and submits it; the PR
 * flow's approvers decide; the finance reviewer sees every SAP entry, adjusts lines and approves, which
 * posts per receipt one A/P invoice and its payment (a cash payment, or a journal entry for a holder with
 * an auxiliary). When SAP refuses a receipt, finance corrects it and posts again what SAP lacks.
 */

interface HeaderState {
  holderUserId: number | null;
  allocationType: AllocationType;
  allocationCode: string;
  allocationName: string | null;
  projectCode: string | null;
  dims: Dims;
  facilityType: string;
  cycle: string;
  purpose: string;
}

type Action =
  | 'approve' | 'reject' | 'revert'
  | 'fin-approve' | 'fin-return' | 'fin-reject' | 'fin-revert'
  | 'cancel';

const ACTION_TEXT: Record<Action, { title: string; button: string; commentRequired: boolean; help: string }> = {
  approve: { title: 'Approve the claim', button: 'Approve', commentRequired: false, help: 'The claim moves to the next stage.' },
  reject: { title: 'Reject the claim', button: 'Reject', commentRequired: true, help: 'The claim ends here; the holder is told why.' },
  revert: { title: 'Send the claim back', button: 'Send back', commentRequired: true, help: 'The claim goes back one stage, or to the holder from the first stage.' },
  'fin-approve': { title: 'Approve and post to SAP', button: 'Approve and post', commentRequired: false, help: 'PRPO creates one A/P invoice and one cash payment per receipt (disallowed receipts are left out).' },
  'fin-return': { title: 'Return to the holder', button: 'Return', commentRequired: true, help: 'The holder can correct the claim and submit it again; it then starts again at stage 1.' },
  'fin-reject': { title: 'Reject the claim', button: 'Reject', commentRequired: true, help: 'The claim ends here; the holder is told why.' },
  'fin-revert': { title: 'Send back to the previous stage', button: 'Send back', commentRequired: true, help: 'The claim goes back to the previous finance stage, whose approvers decide again.' },
  cancel: { title: 'Cancel the claim', button: 'Cancel claim', commentRequired: false, help: 'The claim is withdrawn and can no longer be submitted.' },
};

const IMPREST_EMPTY: ImprestText = { advances: '', cashInHand: '', note: '' };

/** The imprest figures as the claim's Save sends them; throws when a typed figure is not an amount. */
function imprestInput(text: ImprestText): ImprestInput {
  const advances = parseOptionalAmount(text.advances);
  if (!advances.ok) throw new Error('Imprest check: the advances and bills not yet claimed are not an amount');
  const cash = parseOptionalAmount(text.cashInHand);
  if (!cash.ok) throw new Error('Imprest check: the cash in hand is not an amount');
  return { advances: advances.value, cashInHand: cash.value, note: text.note.trim() || null };
}

const errorText = (e: any, fallback: string): string => {
  const data = e?.response?.data;
  const base = String(data?.message || e?.message || fallback);
  // The submit refusal already lists its errors in the message: add only those it does not name.
  const list: string[] = (Array.isArray(data?.errors) ? data.errors.map(String) : []).filter((x: string) => !base.includes(x));
  return list.length ? `${base}: ${list.join('; ')}` : base;
};

const INPUT_KEYS: Array<keyof ClaimLineInput> = [
  'lineId', 'lineNum', 'lineType', 'cardCode', 'supplierName', 'supplierVat', 'invoiceNo', 'invoiceDate', 'amount', 'vatRate',
  'vatAmount', 'total', 'vatCode', 'categoryId', 'auxiliary', 'description', 'buyerNamed', 'qr',
];
const lineInput = (l: ScreenLine, lineNum: number): ClaimLineInput => {
  const out: any = {};
  for (const k of INPUT_KEYS) out[k] = (l as any)[k];
  out.lineNum = lineNum;
  if (!out.lineId) delete out.lineId;
  return out as ClaimLineInput;
};
const cleanDims = (d: Dims): Dims => Object.fromEntries(Object.entries(d || {}).filter(([, v]) => String(v || '').trim() !== '')) as Dims;

/** A dimension code picked from the company's SAP codes (loaded once). */
function DimField({ id, label, value, companyId, dimension, disabled, onChange, helperText }: {
  id: string; label: string; value: string; companyId: number; dimension: number; disabled: boolean; onChange: (v: string) => void; helperText?: string;
}) {
  const [options, setOptions] = useState<CodeName[] | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || options || !companyId) return;
    pettyCashApi.dimensionCodes(companyId, dimension).then(setOptions).catch(() => setOptions([]));
  }, [open, options, companyId, dimension]);
  const current = value ? (options?.find((o) => o.code === value) || { code: value, name: '' }) : null;
  return (
    <Autocomplete
      id={id}
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={current}
      options={options || (current ? [current] : [])}
      loading={open && !options}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
      onChange={(_e, v) => onChange(v?.code || '')}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
    />
  );
}

const PettyCashClaim: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNewRoute = !id || id === 'new';
  const routeClaimId = isNewRoute ? null : Number(id);

  const [detail, setDetail] = useState<ClaimDetail | null>(null);
  const [config, setConfig] = useState<PettyCashConfig | null>(null);
  const [companyId, setCompanyId] = useState<number>(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [header, setHeader] = useState<HeaderState>({
    holderUserId: null, allocationType: 'Project', allocationCode: '', allocationName: null, projectCode: null, dims: {}, facilityType: '', cycle: '', purpose: '',
  });
  const [lines, setLines] = useState<ScreenLine[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [allocOptions, setAllocOptions] = useState<CodeName[]>([]);
  const [projectOptions, setProjectOptions] = useState<CodeName[]>([]);
  const [contractType, setContractType] = useState<string | null>(null);
  const [route, setRoute] = useState<RoutePreview | null>(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [lineDialog, setLineDialog] = useState<{ open: boolean; index: number | null; type: LineType }>({ open: false, index: null, type: 'VAT' });
  const [importOpen, setImportOpen] = useState(false);
  const [financeLine, setFinanceLine] = useState<ScreenLine | null>(null);
  // "Define this supplier" from a receipt; the Suppliers dialog answers with the new vendor.
  const [defineSupplier, setDefineSupplier] = useState<{ vat: string; name: string } | null>(null);
  const [definedVendor, setDefinedVendor] = useState<{ cardCode: string; cardName: string; vat: string } | null>(null);
  // Without the Suppliers function: who in the company can create the vendor (null while loading).
  const [supplierCreators, setSupplierCreators] = useState<SupplierCreator[] | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [comment, setComment] = useState('');
  const [postingDate, setPostingDate] = useState(todayIso());
  const [tab, setTab] = useState<'receipts' | 'accounting' | 'history'>('receipts');
  const [accounting, setAccounting] = useState<AccountingPreview | null>(null);
  const [accountingBusy, setAccountingBusy] = useState(false);
  const [accountingError, setAccountingError] = useState<string | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  // "Post again" after a posting failure: an optional new posting date for what is left.
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryDate, setRetryDate] = useState('');
  const [retryError, setRetryError] = useState<string | null>(null);
  // Imprest check (typed by the holder, saved with the claim), reimbursement, original receipts, voucher.
  const [imprestText, setImprestText] = useState<ImprestText>(IMPREST_EMPTY);
  const [reimburseOpen, setReimburseOpen] = useState(false);
  // The mode stays while the dialog closes.
  const [originalsDialog, setOriginalsDialog] = useState<{ open: boolean; mode: 'mark' | 'remove' }>({ open: false, mode: 'mark' });
  const [voucherBusy, setVoucherBusy] = useState(false);
  const shownClaimId = useRef<number | null>(null);
  const lastStatus = useRef<string | null>(null);

  const claimId = detail?.claim.claimId ?? null;
  const status = detail?.claim.status ?? 'Draft';
  const editable = !detail ? true : detail.permissions.canEdit;
  const vatRegistered = !!config?.company.vatRegistered;
  const currency = detail?.claim.currency || config?.company.currency || '';
  // Where the claim is among the finance stages; only the last one approves the posting (with its date).
  const finStage = useMemo(() => financeStageOf(detail?.finance, detail?.approval?.stages), [detail]);
  const datedReview = !!detail?.permissions.canFinanceReview && finStage.isLast;

  const applyDetail = useCallback((d: ClaimDetail) => {
    shownClaimId.current = d.claim.claimId;
    setDetail(d);
    setLines(d.lines as ScreenLine[]);
    setImprestText(d.imprest
      ? { advances: amountInput(d.imprest.advances), cashInHand: amountInput(d.imprest.cashInHand), note: d.imprest.note || '' }
      : IMPREST_EMPTY);
    setHeader({
      holderUserId: d.claim.holderUserId,
      allocationType: d.claim.allocationType,
      allocationCode: d.claim.allocationCode,
      allocationName: d.claim.allocationName,
      projectCode: d.claim.projectCode,
      dims: { ...(d.claim.dims || {}) },
      facilityType: d.claim.facilityType || '',
      cycle: d.claim.cycle || '',
      purpose: d.claim.purpose || '',
    });
    setContractType(d.claim.contractType);
    if (d.claim.postingDate) setPostingDate(d.claim.postingDate.slice(0, 10));
    setDirty(false);
  }, []);

  // Load: the claim (existing) and the company's petty cash configuration.
  useEffect(() => {
    let cancelled = false;
    if (routeClaimId !== null && shownClaimId.current === routeClaimId) return undefined;
    setLoadError(null);
    (async () => {
      try {
        let company = Number(localStorage.getItem('selectedCompanyId') || '') || 0;
        let d: ClaimDetail | null = null;
        if (routeClaimId !== null) {
          if (!Number.isInteger(routeClaimId) || routeClaimId <= 0) throw new Error('Invalid claim');
          d = await pettyCashApi.getClaim(routeClaimId);
          company = d.claim.companyId;
        }
        if (!company) throw new Error('Select a company first.');
        const cfg = await pettyCashApi.config(company);
        if (cancelled) return;
        setCompanyId(company);
        setConfig(cfg);
        if (d) {
          applyDetail(d);
        } else {
          const f = cfg.routeOptions.facilityTypes;
          const c = cfg.routeOptions.cycles;
          setHeader((h) => ({
            ...h,
            holderUserId: cfg.holder?.userId ?? (cfg.capabilities.canPrepareForOthers && cfg.holders.length === 1 ? cfg.holders[0].userId : null),
            facilityType: f.includes('NA') ? 'NA' : f[0] || 'NA',
            cycle: c.includes('Project') ? 'Project' : c[0] || 'Project',
          }));
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(errorText(e, 'The claim could not be loaded'));
      }
    })();
    return () => { cancelled = true; };
  }, [routeClaimId, applyDetail]);

  const canCreateSuppliers = !!config?.capabilities.suppliers;
  useEffect(() => {
    if (!defineSupplier || canCreateSuppliers || !companyId) return undefined;
    let cancelled = false;
    setSupplierCreators(null);
    supplierApi.creators(companyId)
      .then((rows) => { if (!cancelled) setSupplierCreators(rows); })
      .catch(() => { if (!cancelled) setSupplierCreators([]); });
    return () => { cancelled = true; };
  }, [defineSupplier, canCreateSuppliers, companyId]);

  // Project or department choices (and projects for a cost center claim) while the header is editable:
  // the holder's grants, which the server checks on save (finance may prepare the claim for a holder).
  const grantsOf = config?.capabilities.canPrepareForOthers ? header.holderUserId : null;
  useEffect(() => {
    if (!companyId || !editable) return;
    let cancelled = false;
    pettyCashApi.allocations(companyId, header.allocationType, grantsOf)
      .then((rows) => { if (!cancelled) setAllocOptions(rows); })
      .catch(() => { if (!cancelled) setAllocOptions([]); });
    if (header.allocationType === 'CostCenter') {
      pettyCashApi.allocations(companyId, 'Project', grantsOf)
        .then((rows) => { if (!cancelled) setProjectOptions(rows); })
        .catch(() => { if (!cancelled) setProjectOptions([]); });
    }
    return () => { cancelled = true; };
  }, [companyId, editable, header.allocationType, grantsOf]);

  // The approval route a draft would follow.
  useEffect(() => {
    if (!companyId || !editable || !header.allocationCode || !header.facilityType || !header.cycle) { setRoute(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      setRouteBusy(true);
      pettyCashApi.routePreview({
        companyId, allocationType: header.allocationType, allocationCode: header.allocationCode, facilityType: header.facilityType, cycle: header.cycle, holderUserId: header.holderUserId,
      })
        .then((r) => { if (!cancelled) setRoute(r); })
        .catch((e: any) => { if (!cancelled) setRoute({ flowName: null, stages: [], financeRole: config?.settings.reviewRole || '', error: errorText(e, 'The approval route could not be read') }); })
        .finally(() => { if (!cancelled) setRouteBusy(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [companyId, editable, header.allocationType, header.allocationCode, header.facilityType, header.cycle, header.holderUserId, config?.settings.reviewRole]);

  // The accounting preview (finance review, and after posting). The posting date is chosen at the last finance stage.
  const loadAccounting = useCallback(async () => {
    if (!claimId) return;
    setAccountingBusy(true);
    setAccountingError(null);
    try {
      setAccounting(await pettyCashApi.accounting(claimId, datedReview ? postingDate : undefined));
    } catch (e: any) {
      setAccountingError(errorText(e, 'The SAP entries could not be built'));
    } finally {
      setAccountingBusy(false);
    }
  }, [claimId, datedReview, postingDate]);

  useEffect(() => {
    if (tab === 'accounting' && detail?.permissions.canViewAccounting) void loadAccounting();
  }, [tab, detail?.permissions.canViewAccounting, loadAccounting]);

  // A finance reviewer lands on the accounting.
  useEffect(() => {
    if (detail?.permissions.canFinanceReview) setTab('accounting');
  }, [detail?.claim.claimId, detail?.permissions.canFinanceReview]);

  // While SAP documents (the claim's, or the reimbursement's top-up journal) are being created, refresh every 5 seconds.
  const topUpPosting = detail?.reimbursement?.sap?.status === 'Posting';
  useEffect(() => {
    if ((status !== 'Posting' && !topUpPosting) || !claimId) return undefined;
    const timer = setInterval(() => {
      pettyCashApi.getClaim(claimId).then(applyDetail).catch(() => undefined);
    }, 5000);
    return () => clearInterval(timer);
  }, [status, topUpPosting, claimId, applyDetail]);

  /** The holder's figures (balance, claims not yet posted) after the claim moved: they come with the configuration. */
  const refreshConfig = useCallback(async () => {
    if (!companyId) return;
    try {
      setConfig(await pettyCashApi.config(companyId));
    } catch {
      // Keep the figures shown; a reload brings them.
    }
  }, [companyId]);

  // A posting that finished (Posted or partly posted) changes the holder's "not yet posted" total.
  useEffect(() => {
    const before = lastStatus.current;
    lastStatus.current = status;
    if (before === 'Posting' && status !== 'Posting') void refreshConfig();
  }, [status, refreshConfig]);

  const holderOptions = useMemo(() => {
    if (!config) return [];
    const own = config.holder ? [config.holder] : [];
    const others = config.capabilities.canPrepareForOthers ? config.holders : [];
    const seen = new Set<number>();
    return [...own, ...others].filter((h) => (seen.has(h.userId) ? false : (seen.add(h.userId), true)));
  }, [config]);
  const holder = holderOptions.find((h) => h.userId === header.holderUserId) || null;

  const allocationLabel = header.allocationCode ? `${header.allocationCode}${header.allocationName ? ` ${header.allocationName}` : ''}` : '';
  const headerReady = !!header.allocationCode && !!header.holderUserId && !!header.facilityType && !!header.cycle;

  const setHeaderField = <K extends keyof HeaderState>(key: K, value: HeaderState[K]) => {
    setHeader((h) => ({ ...h, [key]: value }));
    setDirty(true);
  };

  /** The chosen project's division, department, location and auxiliary, and its contract type. */
  const applyProject = async (code: string, keepDepartment: boolean) => {
    if (!code || !companyId) { setContractType(null); return; }
    try {
      const p = await pettyCashApi.projectDefaults(companyId, code);
      setContractType(p.contractType);
      setHeader((h) => {
        const dims: Dims = { ...h.dims, ...p.dims };
        if (keepDepartment && h.allocationCode) dims['2'] = h.allocationCode;
        return { ...h, dims };
      });
    } catch {
      setContractType(null);
    }
  };

  const buildInput = (ls: ScreenLine[], h: HeaderState): ClaimSaveInput => ({
    companyId,
    holderUserId: h.holderUserId,
    allocationType: h.allocationType,
    allocationCode: h.allocationCode,
    projectCode: h.allocationType === 'CostCenter' ? (h.projectCode || null) : null,
    dims: cleanDims(h.dims),
    facilityType: h.facilityType,
    cycle: h.cycle,
    purpose: h.purpose.trim() || null,
    lines: ls.map((l, i) => lineInput(l, i + 1)),
    // The imprest figures go with every save while the holder may change them.
    ...(detail?.permissions.canEditImprest && detail.permissions.canEdit && detail.imprest ? { imprest: imprestInput(imprestText) } : {}),
  });

  /** Saves header and lines (creates the claim first time), then uploads chosen receipt files. */
  const persist = async (ls: ScreenLine[], h: HeaderState, files: Map<number, File> = new Map()): Promise<ClaimDetail> => {
    const input = buildInput(ls, h);
    let d = claimId ? await pettyCashApi.saveClaim(claimId, input) : await pettyCashApi.createClaim(input);
    if (!claimId) {
      shownClaimId.current = d.claim.claimId;
      navigate(`/petty-cash/${d.claim.claimId}`, { replace: true });
    }
    // Shown at once: if a file is refused below, the next save updates this claim instead of creating another.
    applyDetail(d);
    if (!files.size) return d;
    try {
      for (const [lineNum, file] of files) {
        const saved = d.lines.find((l) => l.lineNum === lineNum);
        if (saved) await pettyCashApi.uploadAttachment(d.claim.claimId, file, saved.lineId);
      }
    } catch (e: any) {
      throw Object.assign(new Error(`The receipt is saved, but its file was not attached: ${errorText(e, 'the upload failed')}`), { lineSaved: true });
    } finally {
      const fresh = await pettyCashApi.getClaim(d.claim.claimId).catch(() => null);
      if (fresh) { d = fresh; applyDetail(fresh); }
    }
    return d;
  };

  const saveDraft = async () => {
    setBusy('save');
    setPageError(null);
    try {
      await persist(lines, header);
      dispatch(showSnackbar({ message: 'Draft saved', severity: 'success' }));
    } catch (e: any) {
      setPageError(errorText(e, 'The claim could not be saved'));
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    setBusy('submit');
    setPageError(null);
    try {
      const d = dirty || !claimId ? await persist(lines, header) : detail!;
      const after = await pettyCashApi.submitClaim(d.claim.claimId);
      applyDetail(after);
      void refreshConfig();
      dispatch(showSnackbar({ message: `${after.claim.claimNo || 'The claim'} was submitted for approval`, severity: 'success' }));
    } catch (e: any) {
      setPageError(errorText(e, 'The claim could not be submitted'));
      if (claimId) pettyCashApi.getClaim(claimId).then(applyDetail).catch(() => undefined);
    } finally {
      setBusy(null);
    }
  };

  const onSaveLine = async (line: ClaimLineInput, file: File | null) => {
    const index = lineDialog.index;
    const next = [...lines];
    if (index === null) next.push({ ...line });
    else next[index] = { ...next[index], ...line };
    const position = index === null ? next.length : index + 1;
    const files = new Map<number, File>();
    if (file) files.set(position, file);
    try {
      await persist(next, header, files);
    } catch (e: any) {
      // Saved without its file: the dialog now edits that saved receipt, so trying again only attaches the file.
      if (e?.lineSaved && index === null) setLineDialog((st) => ({ ...st, index: position - 1 }));
      throw e;
    }
    setLineDialog((st) => ({ ...st, open: false }));
  };

  const onImport = async (imported: ClaimLineInput[]) => {
    setImportOpen(false);
    setBusy('save');
    setPageError(null);
    try {
      await persist([...lines, ...imported.map((l) => ({ ...l }))], header);
      dispatch(showSnackbar({ message: `${imported.length} receipt${imported.length === 1 ? '' : 's'} added; attach each receipt's photo from its line`, severity: 'success' }));
    } catch (e: any) {
      setPageError(errorText(e, 'The receipts could not be added'));
    } finally {
      setBusy(null);
    }
  };

  const removeLine = async (index: number) => {
    setDeleteIndex(null);
    setBusy('save');
    setPageError(null);
    try {
      await persist(lines.filter((_l, i) => i !== index), header);
    } catch (e: any) {
      setPageError(errorText(e, 'The receipt could not be removed'));
    } finally {
      setBusy(null);
    }
  };

  const openAttachment = async (a: ClaimAttachment) => {
    const w = window.open('', '_blank');
    try {
      const blob = await pettyCashApi.downloadAttachment(a.attachmentId);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url;
      else {
        const link = document.createElement('a');
        link.href = url;
        link.download = a.fileName;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      if (w) w.close();
      dispatch(showSnackbar({ message: errorText(e, 'The file could not be opened'), severity: 'error' }));
    }
  };

  const deleteAttachment = async (a: ClaimAttachment) => {
    try {
      await pettyCashApi.deleteAttachment(a.attachmentId);
      if (claimId) applyDetail(await pettyCashApi.getClaim(claimId));
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorText(e, 'The file could not be removed'), severity: 'error' }));
    }
  };

  const saveFinanceLine = async (change: FinanceLineChange) => {
    if (!claimId || !financeLine?.lineId) return;
    const d = await pettyCashApi.financeLine(claimId, financeLine.lineId, change);
    applyDetail(d);
    setFinanceLine(null);
    if (tab === 'accounting') void loadAccounting();
  };

  const runAction = async () => {
    if (!action || !claimId) return;
    const text = ACTION_TEXT[action];
    if (text.commentRequired && !comment.trim()) return;
    setBusy(action);
    setPageError(null);
    try {
      let d: ClaimDetail;
      const c = comment.trim() || undefined;
      if (action === 'approve' || action === 'reject' || action === 'revert') d = await pettyCashApi.decide(claimId, action, c);
      else if (action === 'fin-approve') d = await pettyCashApi.finance(claimId, 'approve', c, finStage.isLast ? postingDate : undefined);
      else if (action === 'fin-return') d = await pettyCashApi.finance(claimId, 'return', c);
      else if (action === 'fin-reject') d = await pettyCashApi.finance(claimId, 'reject', c);
      else if (action === 'fin-revert') d = await pettyCashApi.finance(claimId, 'revert', c);
      else d = await pettyCashApi.cancelClaim(claimId, c);
      applyDetail(d);
      setAction(null);
      setComment('');
      void refreshConfig();
      const after = financeStageOf(d.finance, d.approval?.stages);
      const where = d.claim.status === 'FinanceReview' && after.count > 1 && after.name ? ` (${after.name})` : '';
      dispatch(showSnackbar({ message: `${d.claim.claimNo || 'Claim'}: ${statusLook(d.displayStatus || d.claim.status).label}${where}`, severity: 'success' }));
    } catch (e: any) {
      setPageError(errorText(e, 'The action failed'));
      setAction(null);
    } finally {
      setBusy(null);
    }
  };

  const claimPostingDate = detail?.claim.postingDate ? detail.claim.postingDate.slice(0, 10) : '';
  const openRetry = () => {
    setRetryDate(claimPostingDate || todayIso());
    setRetryError(null);
    setRetryOpen(true);
  };

  const retry = async () => {
    if (!claimId) return;
    const date = cleanDateInput(retryDate);
    if (date && claimPostingDate && date < claimPostingDate) { setRetryError(`The posting date cannot be earlier than ${formatDate(claimPostingDate)}.`); return; }
    if (date && date > todayIso()) { setRetryError('The posting date cannot be in the future.'); return; }
    setBusy('retry');
    setRetryError(null);
    setPageError(null);
    try {
      const d = await pettyCashApi.retry(claimId, date && date !== claimPostingDate ? date : undefined);
      applyDetail(d);
      setRetryOpen(false);
      void refreshConfig();
      dispatch(showSnackbar({ message: d.claim.status === 'Posted' ? 'Every receipt is now posted in SAP' : 'Posting again; see each receipt\'s status', severity: d.claim.status === 'Posted' ? 'success' : 'info' }));
    } catch (e: any) {
      // 422 lists what must be fixed first (a receipt still without its auxiliary...).
      setRetryError(errorText(e, 'The posting could not be retried'));
    } finally {
      setBusy(null);
    }
  };

  /** Finance records how the holder was paid back (the dialog shows a refusal). */
  const recordReimbursement = async (input: ReimburseInput) => {
    if (!claimId) return;
    const d = await pettyCashApi.reimburse(claimId, input);
    applyDetail(d);
    setReimburseOpen(false);
    void refreshConfig();
    const refused = d.reimbursement?.sap?.status === 'Failed';
    dispatch(showSnackbar({
      message: refused ? 'Reimbursement recorded, but SAP refused the top-up journal: see the reason on the claim' : 'Reimbursement recorded',
      severity: refused ? 'warning' : 'success',
    }));
  };

  const retryReimbursement = async () => {
    if (!claimId) return;
    setBusy('reimburse-retry');
    setPageError(null);
    try {
      const d = await pettyCashApi.retryReimbursement(claimId);
      applyDetail(d);
      const sapStatus = d.reimbursement?.sap?.status;
      dispatch(showSnackbar({
        message: sapStatus === 'Posted' ? 'The top-up journal is posted in SAP' : sapStatus === 'Failed' ? 'SAP refused the top-up journal again' : 'Posting the top-up journal again',
        severity: sapStatus === 'Posted' ? 'success' : sapStatus === 'Failed' ? 'error' : 'info',
      }));
    } catch (e: any) {
      setPageError(errorText(e, 'The top-up journal could not be posted again'));
    } finally {
      setBusy(null);
    }
  };

  const saveOriginals = async (input: { received: boolean; reference: string | null; note: string | null }) => {
    if (!claimId) return;
    const d = await pettyCashApi.markOriginals(claimId, input);
    applyDetail(d);
    setOriginalsDialog((o) => ({ ...o, open: false }));
    dispatch(showSnackbar({ message: input.received ? 'Original receipts marked as received' : 'Mark removed', severity: 'success' }));
  };

  const printVoucher = async () => {
    if (!claimId) return;
    setVoucherBusy(true);
    try {
      const { blob, fileName } = await pettyCashApi.downloadVoucher(claimId, detail?.claim.claimNo);
      saveBlob(blob, fileName);
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorText(e, 'The voucher could not be built'), severity: 'error' }));
    } finally {
      setVoucherBusy(false);
    }
  };

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/petty-cash')}>Petty cash claims</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{loadError}</Alert>
      </Box>
    );
  }
  if (!config) {
    return <Box sx={{ p: 3, display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={20} /><Typography>Loading…</Typography></Box>;
  }

  const perms = detail?.permissions;
  const displayStatus = detail?.displayStatus || status;
  const activeDims = config.dimensions.filter((d) => d.active);
  // The header's dimensions: the auxiliary (Dimension 5) is chosen on each receipt, from the codes its account takes.
  const headerDims = activeDims.filter((d) => d.code !== 5);
  const isBusy = busy !== null;
  const lineForDialog = lineDialog.index !== null ? lines[lineDialog.index] : null;
  const posted = status === 'Posted';
  const showPosting = ['Posting', 'Posted', 'PartiallyPosted'].includes(status);
  const visibleTab = tab === 'accounting' && !perms?.canViewAccounting ? 'receipts' : tab;
  // After a posting failure: the receipts SAP refused, and the ones still to post (Post again sends only these).
  const correcting = status === 'PartiallyPosted';
  const refused = lines.filter((l) => l.status === 'Failed');
  const toPost = lines.filter((l) => l.status && l.status !== 'Paid' && l.status !== 'Disallowed');
  // Only the first finance stage (the review) changes the receipts; the later stages approve what it left.
  const laterFinanceStage = !!perms?.canFinanceReview && (finStage.position ?? 1) > 1;
  const canChangeLines = (!!perms?.canFinanceReview && !laterFinanceStage) || !!perms?.canCorrect;
  const shownLines: ScreenLine[] = laterFinanceStage ? lines.map((l) => ({ ...l, canCorrect: false })) : lines;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  // Finance approval: each receipt's payment is a journal entry when the holder has an auxiliary on a shared cash account.
  const finApproveHelp = detail?.claim.auxiliaryCode
    ? `PRPO creates one A/P invoice per receipt, each paid by a journal entry from ${detail.claim.sapAccount || 'the cash account'} with the holder's auxiliary ${detail.claim.auxiliaryCode} and reconciled with it (disallowed receipts are left out).`
    : ACTION_TEXT['fin-approve'].help;
  const stageText = finStage.count > 1 && finStage.position ? `Stage ${finStage.position} of ${finStage.count}${finStage.name ? `: ${finStage.name}` : ''}` : null;
  const process = processSettings(config.settings);
  // The originals must be marked received before the last finance approval (when the setup says so).
  const originalsMissing = !!detail?.originals?.required && !detail.originals.receivedAt;
  const imprest = detail?.imprest;
  const showImprest = !!imprest && !(imprest.floatAmount === null && imprest.checkMode === 'off');
  const showReimbursement = !!detail && (!!detail.reimbursement || (status === 'Posted' && (!!perms?.canReimburse || !!detail.target)));
  const showOriginals = !!detail?.originals && status !== 'Draft' && status !== 'Cancelled'
    && (detail.originals.required || !!detail.originals.receivedAt || !!perms?.canMarkOriginals);
  const showTarget = !!detail?.target && (WAITING_STATUSES.includes(status) || displayStatus === 'Reimbursed');
  // The imprest card shows its own checks.
  const claimChecks = (detail?.checks || []).filter((c) => !(showImprest && c.code.startsWith('imprest-')));
  const approveDisabled = finStage.isLast && (originalsMissing || !/^\d{4}-\d{2}-\d{2}$/.test(postingDate));
  const paidAmount = paidTotal(lines.map((l) => ({ total: l.total, status: l.status })));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton aria-label="Back to the claims" onClick={() => navigate('/petty-cash')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {detail?.claim.claimNo ? `Petty cash claim ${detail.claim.claimNo}` : 'New petty cash claim'}
        </Typography>
        <ClaimStatusChip status={displayStatus} />
        {showTarget && detail?.target ? (
          <WaitingChip days={detail.target.waitingWorkingDays} target={detail.target.targetDays} overdue={detail.target.overdue} reimbursed={displayStatus === 'Reimbursed'} />
        ) : null}
        {dirty && editable ? <Typography variant="caption" color="text.secondary">Unsaved changes</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        {perms?.canPrintVoucher ? (
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => { void printVoucher(); }} disabled={voucherBusy}>
            {voucherBusy ? 'Preparing…' : 'Print voucher'}
          </Button>
        ) : null}
        {editable ? (
          <>
            <Button variant="outlined" onClick={() => { void saveDraft(); }} disabled={isBusy || !headerReady}>{busy === 'save' ? 'Saving…' : 'Save draft'}</Button>
            <Button onClick={() => { void submit(); }} disabled={isBusy || !headerReady || !lines.length}>{busy === 'submit' ? 'Submitting…' : 'Submit for approval'}</Button>
          </>
        ) : null}
        {perms?.canCancel ? <Button variant="outlined" onClick={() => setAction('cancel')} disabled={isBusy}>Cancel claim</Button> : null}
        {perms?.canDecide ? (
          <>
            <Button variant="outlined" onClick={() => setAction('revert')} disabled={isBusy}>Send back</Button>
            <Button variant="outlined" color="error" onClick={() => setAction('reject')} disabled={isBusy}>Reject</Button>
            <Button onClick={() => setAction('approve')} disabled={isBusy}>Approve</Button>
          </>
        ) : null}
        {perms?.canRevertFinance ? <Button variant="outlined" onClick={() => setAction('fin-revert')} disabled={isBusy}>Send back to the previous stage</Button> : null}
        {perms?.canFinanceReview ? (
          <>
            <Button variant="outlined" onClick={() => setAction('fin-return')} disabled={isBusy}>Return to holder</Button>
            <Button variant="outlined" color="error" onClick={() => setAction('fin-reject')} disabled={isBusy}>Reject</Button>
            <Button onClick={() => setAction('fin-approve')} disabled={isBusy}>{finStage.isLast ? 'Approve and post to SAP' : 'Approve'}</Button>
          </>
        ) : null}
        {perms?.canRetry ? <Button onClick={openRetry} disabled={isBusy}>{busy === 'retry' ? 'Posting…' : 'Post again'}</Button> : null}
        {perms?.canReimburse && !detail?.reimbursement ? <Button onClick={() => setReimburseOpen(true)} disabled={isBusy}>Record reimbursement</Button> : null}
      </Stack>

      {!config.company.pettyCashEnabled ? <Alert severity="warning">Petty cash is not switched on for {config.company.name} (Company Management).</Alert> : null}
      {pageError ? <Alert severity="error" onClose={() => setPageError(null)} sx={{ whiteSpace: 'pre-line' }}>{pageError}</Alert> : null}
      {detail?.claim.lastComment && ['Returned', 'Rejected'].includes(status) ? (
        <Alert severity="warning">{status === 'Returned' ? 'Returned' : 'Rejected'}: {detail.claim.lastComment}</Alert>
      ) : null}
      {status === 'Posting' ? <Alert severity="info" icon={<CircularProgress size={18} />}>PRPO is creating the SAP documents; this page refreshes by itself.</Alert> : null}
      {correcting ? (
        <Alert severity="error">
          {refused.length ? `SAP refused ${plural(refused.length, 'receipt')}. ` : `${plural(toPost.length, 'receipt')} ${toPost.length === 1 ? 'is' : 'are'} not in SAP yet. `}
          {perms?.canCorrect
            ? `Correct ${refused.length === 1 ? 'it' : 'them'} (account, VAT code, dimensions, or take the receipt out), then press Post again. Receipts already in SAP are not posted twice.`
            : 'Finance corrects the refused receipts and posts them again.'}
        </Alert>
      ) : null}
      {perms?.canFinanceReview && stageText ? (
        <Alert severity="info">
          {stageText}.{' '}
          {laterFinanceStage
            ? 'The receipts were checked and changed at the finance review (stage 1): this stage approves, sends the claim back to the previous stage, returns or rejects it.'
            : `After your review the claim goes to ${finStage.nextName || 'the next finance stage'}.`}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {editable && !detail && config.capabilities.canPrepareForOthers ? (
              <TextField
                id="pc-holder"
                select
                label="Petty cash holder"
                value={header.holderUserId ?? ''}
                onChange={(e) => setHeaderField('holderUserId', Number(e.target.value) || null)}
                fullWidth
                required
              >
                {holderOptions.map((h) => <MenuItem key={h.userId} value={h.userId}>{h.userName}</MenuItem>)}
              </TextField>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">Petty cash holder</Typography>
                <Typography variant="body2" fontWeight={600}>{detail?.claim.holderName || holder?.userName || 'You are not a petty cash holder in this company'}</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">SAP petty cash account</Typography>
            <Typography variant="body2">{detail?.claim.sapAccount ? `${detail.claim.sapAccount} ${detail.claim.sapAccountName || ''}` : holder ? `${holder.sapAccount} ${holder.sapAccountName || ''}` : '—'}</Typography>
            {(detail?.claim.sapAccount ? detail.claim.auxiliaryCode : holder?.auxiliaryCode) ? (
              <Typography variant="caption" color="text.secondary">
                Auxiliary {detail?.claim.sapAccount ? `${detail.claim.auxiliaryCode} ${detail.claim.auxiliaryName || ''}` : `${holder?.auxiliaryCode} ${holder?.auxiliaryName || ''}`}
              </Typography>
            ) : null}
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Float</Typography>
            <Typography variant="body2">{holder?.floatAmount ? `${formatAmount(holder.floatAmount)} ${currency}` : '—'}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">SAP balance</Typography>
            <Typography variant="body2">{holder?.balance !== undefined && holder?.balance !== null ? `${formatAmount(holder.balance)} ${currency}` : holder?.auxiliaryCode ? 'Shared account' : '—'}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Claims not yet posted</Typography>
            <Typography variant="body2">{holder?.pendingTotal !== undefined ? `${formatAmount(holder.pendingTotal)} ${currency}` : '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>Header</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {editable ? (
              <RadioGroup
                row
                aria-label="Allocation type"
                value={header.allocationType}
                onChange={(e) => {
                  const t = e.target.value as AllocationType;
                  setHeader((h) => ({ ...h, allocationType: t, allocationCode: '', allocationName: null, projectCode: null, dims: {} }));
                  setContractType(null);
                  setDirty(true);
                }}
              >
                <FormControlLabel value="Project" control={<Radio size="small" />} label="Project" disabled={lines.length > 0} />
                <FormControlLabel value="CostCenter" control={<Radio size="small" />} label="Cost center" disabled={lines.length > 0} />
              </RadioGroup>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">Allocation</Typography>
                <Typography variant="body2">{header.allocationType === 'Project' ? 'Project' : 'Cost center'}</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {editable ? (
              <Autocomplete
                id="pc-allocation"
                size="small"
                options={allocOptions}
                value={header.allocationCode ? (allocOptions.find((o) => o.code === header.allocationCode) || { code: header.allocationCode, name: header.allocationName || '' }) : null}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
                onChange={(_e, v) => {
                  setHeader((h) => ({ ...h, allocationCode: v?.code || '', allocationName: v?.name || null, dims: h.allocationType === 'CostCenter' && v ? { ...h.dims, 2: v.code } : h.dims }));
                  setDirty(true);
                  if (header.allocationType === 'Project') void applyProject(v?.code || '', false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={`${header.allocationType === 'Project' ? 'Project' : 'Department'} (${grantsOf && grantsOf !== config.holder?.userId ? 'the holder\'s' : 'your'} granted ${header.allocationType === 'Project' ? 'projects' : 'departments'})`}
                    helperText={lines.length ? 'Remove the receipts to change the allocation type' : undefined}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  />
                )}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">{header.allocationType === 'Project' ? 'Project' : 'Department'}</Typography>
                <Typography variant="body2">{allocationLabel || '—'}</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary">Contract type (from SAP)</Typography>
            <Typography variant="body2">{contractType || '—'}</Typography>
            {detail?.claim.glWarehouseCode ? (
              <Typography variant="caption" color="text.secondary" display="block">
                G/L rules of warehouse {detail.claim.glWarehouseCode}{detail.claim.glWarehouseType ? ` (${detail.claim.glWarehouseType})` : ''}
              </Typography>
            ) : null}
          </Grid>
          {header.allocationType === 'CostCenter' ? (
            <Grid item xs={12} md={6}>
              {editable ? (
                <Autocomplete
                  id="pc-project"
                  size="small"
                  options={projectOptions}
                  value={header.projectCode ? (projectOptions.find((o) => o.code === header.projectCode) || { code: header.projectCode, name: '' }) : null}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
                  onChange={(_e, v) => {
                    setHeaderField('projectCode', v?.code || null);
                    void applyProject(v?.code || '', true);
                  }}
                  renderInput={(params) => <TextField {...params} label="Project on the receipts" helperText="Required where the company's PR rule asks for a project on every line" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Project on the receipts</Typography>
                  <Typography variant="body2">{header.projectCode || '—'}</Typography>
                </Box>
              )}
            </Grid>
          ) : null}
          {headerDims.map((d) => {
            const key = String(d.code);
            return (
              <Grid item xs={12} sm={6} md={headerDims.length <= 4 || header.allocationType === 'CostCenter' ? 3 : 2.4} key={key}>
                {editable ? (
                  <DimField
                    id={`pc-dim-${key}`}
                    label={d.label}
                    value={header.dims[key] || ''}
                    companyId={companyId}
                    dimension={d.code}
                    disabled={header.allocationType === 'CostCenter' && key === '2'}
                    onChange={(v) => setHeaderField('dims', { ...header.dims, [key]: v })}
                    helperText={header.dims[key] ? undefined : 'Empty: from the expense category'}
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                    <Typography variant="body2">{header.dims[key] || '—'}</Typography>
                  </Box>
                )}
              </Grid>
            );
          })}
          <Grid item xs={6} md={3}>
            {editable ? (
              <TextField id="pc-facility" select label="Facility type" value={header.facilityType} onChange={(e) => setHeaderField('facilityType', e.target.value)} fullWidth required>
                {(config.routeOptions.facilityTypes.length ? config.routeOptions.facilityTypes : ['NA']).map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            ) : (
              <Box><Typography variant="caption" color="text.secondary">Facility type</Typography><Typography variant="body2">{header.facilityType || '—'}</Typography></Box>
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {editable ? (
              <TextField id="pc-cycle" select label="Cycle" value={header.cycle} onChange={(e) => setHeaderField('cycle', e.target.value)} fullWidth required>
                {(config.routeOptions.cycles.length ? config.routeOptions.cycles : ['Project']).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            ) : (
              <Box><Typography variant="caption" color="text.secondary">Cycle</Typography><Typography variant="body2">{header.cycle || '—'}</Typography></Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {editable ? (
              <TextField id="pc-purpose" label="Purpose (shown to approvers)" value={header.purpose} onChange={(e) => setHeaderField('purpose', e.target.value.slice(0, 1000))} fullWidth />
            ) : (
              <Box><Typography variant="caption" color="text.secondary">Purpose</Typography><Typography variant="body2">{header.purpose || '—'}</Typography></Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Approval route</Typography>
        {detail && status !== 'Draft' && status !== 'Returned' ? (
          <ApprovalPath stages={detail.approval.stages} posted={posted} />
        ) : routeBusy ? (
          <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Finding the approval flow…</Typography>
        ) : route ? (
          route.error ? <Alert severity="warning">{route.error}</Alert> : (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">The PR flow {route.flowName ? `"${route.flowName}" ` : ''}a Service PR for {allocationLabel} follows, then the finance review</Typography>
              {route.stages.map((st) => <Typography key={st.step} variant="body2">{st.step} · {st.name}{st.approvers.length ? `: ${st.approvers.join(', ')}` : ''}</Typography>)}
              <Typography variant="body2">{route.stages.length + 1} · Finance review ({route.financeRole})</Typography>
              {process.financeStages.map((f, i) => (
                <Typography key={`fin-${i}`} variant="body2">{route.stages.length + 2 + i} · {f.name} ({f.role})</Typography>
              ))}
            </Stack>
          )
        ) : (
          <Typography variant="body2" color="text.secondary">Choose the {header.allocationType === 'Project' ? 'project' : 'department'}, facility type and cycle to see the approval route.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Tabs value={visibleTab} onChange={(_e, v) => setTab(v)} aria-label="Claim views" sx={{ px: 1, borderBottom: '1px solid #e3e8eb' }} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="receipts" label={`Receipts (${lines.length})`} />
          {perms?.canViewAccounting ? <Tab value="accounting" label="Accounting" /> : null}
          {detail ? <Tab value="history" label="History" /> : null}
        </Tabs>
        <Box sx={{ p: 2 }}>
          {visibleTab === 'receipts' ? (
            <Stack spacing={1.5}>
              {editable ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  {!headerReady ? <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>Fill in the header first: holder, {header.allocationType === 'Project' ? 'project' : 'department'}, facility type and cycle.</Typography> : <Box sx={{ mr: 'auto' }} />}
                  <Button size="small" variant="text" onClick={() => downloadReceiptTemplate(config)}>Download Excel template</Button>
                  <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)} disabled={!headerReady || isBusy}>Import from Excel</Button>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLineDialog({ open: true, index: null, type: 'NonVAT' })} disabled={!headerReady || isBusy}>Add non-VAT receipt</Button>
                  {vatRegistered ? <Button size="small" startIcon={<AddIcon />} onClick={() => setLineDialog({ open: true, index: null, type: 'VAT' })} disabled={!headerReady || isBusy}>Add VAT receipt</Button> : null}
                </Stack>
              ) : null}
              <LinesTable
                lines={shownLines}
                config={config}
                currency={currency}
                allocationType={header.allocationType}
                editable={editable && !isBusy}
                finance={canChangeLines}
                correcting={correcting}
                showPosting={showPosting}
                onEdit={(i) => setLineDialog({ open: true, index: i, type: lines[i].lineType })}
                onDelete={(i) => setDeleteIndex(i)}
                onFinance={(l) => setFinanceLine(l)}
                onOpenAttachment={(a) => { void openAttachment(a); }}
              />
              {claimChecks.length ? <CheckList checks={claimChecks} /> : null}
              {detail?.attachments?.length ? (
                <Typography variant="body2">Claim files: {detail.attachments.map((a) => (
                  <Button key={a.attachmentId} size="small" variant="text" onClick={() => { void openAttachment(a); }}>{a.fileName}</Button>
                ))}</Typography>
              ) : null}
            </Stack>
          ) : null}
          {visibleTab === 'accounting' ? (
            <Stack spacing={2}>
              {datedReview ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField
                    id="pc-posting-date"
                    type="date"
                    label="Posting date"
                    value={postingDate}
                    onChange={(e) => setPostingDate(cleanDateInput(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    helperText="SAP's posting date of every invoice and payment; each receipt keeps its own date as document date"
                    sx={{ maxWidth: 420 }}
                  />
                  <Button variant="outlined" onClick={() => { void loadAccounting(); }} disabled={accountingBusy}>Refresh</Button>
                </Stack>
              ) : perms?.canFinanceReview ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">The posting date is chosen at the last finance stage.</Typography>
                  <Button variant="outlined" onClick={() => { void loadAccounting(); }} disabled={accountingBusy}>Refresh</Button>
                </Stack>
              ) : null}
              <AccountingView
                preview={accounting}
                loading={accountingBusy}
                error={accountingError}
                currency={currency}
                dimensions={activeDims}
                onChangeLine={canChangeLines ? (lineId) => setFinanceLine(lines.find((l) => l.lineId === lineId) || null) : undefined}
                canChangeLine={(lineId) => {
                  const l = shownLines.find((x) => x.lineId === lineId);
                  return !!l && (l.canCorrect ?? !!perms?.canFinanceReview);
                }}
                changeLabel={correcting ? 'Correct' : 'Change'}
              />
            </Stack>
          ) : null}
          {visibleTab === 'history' && detail ? (
            detail.history.length ? (
              <Stack component="ol" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {detail.history.map((h, i) => (
                  <Box component="li" key={`${h.at}-${i}`} sx={{ borderBottom: '1px solid #eceff1', pb: 1 }}>
                    <Typography variant="body2"><strong>{h.action}</strong>{h.stage ? ` · ${h.stage}` : ''}{h.userName ? ` · ${h.userName}` : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDateTime(h.at)}</Typography>
                    {h.comment ? <Typography variant="body2" sx={{ mt: 0.5 }}>{h.comment}</Typography> : null}
                  </Box>
                ))}
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">No history yet.</Typography>
          ) : null}
        </Box>
      </Paper>

      {showImprest || showReimbursement || showOriginals ? (
        <Grid container spacing={2}>
          {showImprest && imprest ? (
            <Grid item xs={12} md={6}>
              <ImprestCard
                view={imprest}
                // Saved with the claim's Save, so only while the claim itself can be saved.
                editable={!!perms?.canEditImprest && editable}
                text={imprestText}
                onChange={(t) => { setImprestText(t); setDirty(true); }}
                currency={currency}
              />
            </Grid>
          ) : null}
          {showReimbursement && detail ? (
            <Grid item xs={12} sm={6} md={showImprest ? 3 : 6}>
              <ReimbursementCard
                reimbursement={detail.reimbursement ?? null}
                currency={currency}
                paidAmount={paidAmount}
                canReimburse={!!perms?.canReimburse}
                canRetry={!!perms?.canRetryReimbursement}
                busy={busy === 'reimburse-retry'}
                onRecord={() => setReimburseOpen(true)}
                onRetry={() => { void retryReimbursement(); }}
              />
            </Grid>
          ) : null}
          {showOriginals && detail?.originals ? (
            <Grid item xs={12} sm={6} md={showImprest ? 3 : 6}>
              <OriginalsCard
                originals={detail.originals}
                canMark={!!perms?.canMarkOriginals}
                busy={isBusy}
                onMark={() => setOriginalsDialog({ open: true, mode: 'mark' })}
                onRemove={() => setOriginalsDialog({ open: true, mode: 'remove' })}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      {detail ? (
        <Typography variant="caption" color="text.secondary">
          Created by {detail.claim.createdByName} {formatDateTime(detail.claim.createdAt)}
          {detail.claim.submittedAt ? ` · submitted ${formatDateTime(detail.claim.submittedAt)}` : ''}
          {detail.claim.postingDate ? ` · posting date ${formatDate(detail.claim.postingDate)}` : ''}
          {detail.claim.postedAt ? ` · posted ${formatDateTime(detail.claim.postedAt)}` : ''}
        </Typography>
      ) : null}

      <ReceiptLineDialog
        open={lineDialog.open}
        companyId={companyId}
        config={config}
        allocationType={header.allocationType}
        allocationCode={header.allocationCode}
        projectCode={header.projectCode}
        allocationLabel={allocationLabel}
        initial={lineForDialog ? lineInput(lineForDialog, lineForDialog.lineNum) : null}
        lineNum={lines.length + 1}
        defaultType={lineDialog.type}
        attachments={lineForDialog?.attachments || []}
        onClose={() => { setLineDialog((x) => ({ ...x, open: false })); setDefinedVendor(null); }}
        onSave={onSaveLine}
        onOpenAttachment={(a) => { void openAttachment(a); }}
        onDeleteAttachment={editable ? deleteAttachment : undefined}
        onDefineSupplier={(vat, name) => setDefineSupplier({ vat, name })}
        definedVendor={definedVendor}
      />

      <LineImportDialog
        open={importOpen}
        companyId={companyId}
        config={config}
        firstLineNum={lines.length + 1}
        onClose={() => setImportOpen(false)}
        onImport={(ls) => { void onImport(ls); }}
      />

      <FinanceLineDialog
        open={!!financeLine}
        companyId={companyId}
        config={config}
        line={financeLine}
        correcting={correcting}
        onClose={() => setFinanceLine(null)}
        onSave={saveFinanceLine}
      />

      {/* A receipt whose VAT number SAP lacks: create the vendor here (Suppliers function), else name who can. */}
      {canCreateSuppliers ? (
        <SupplierQuickCreateDialog
          open={!!defineSupplier}
          companyId={companyId}
          initialVat={defineSupplier?.vat}
          initialName={defineSupplier?.name}
          onClose={() => setDefineSupplier(null)}
          onCreated={(v) => { setDefinedVendor(v); setDefineSupplier(null); }}
        />
      ) : (
        <ResponsiveDialog open={!!defineSupplier} onClose={() => setDefineSupplier(null)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="pc-define-title">
          <DialogTitle id="pc-define-title">Define this supplier</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              SAP has no vendor with VAT number {defineSupplier?.vat}. A user with the Suppliers function creates it (Petty Cash › Suppliers); then type the VAT number on the receipt again.
            </Typography>
            {supplierCreators === null ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Finding who can create suppliers…</Typography>
              </Stack>
            ) : supplierCreators.length ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Can create suppliers in this company:</Typography>
                {supplierCreators.slice(0, 12).map((c) => (
                  <Typography key={c.userId} variant="body2">
                    {c.name}{c.email ? ` · ${c.email}` : ''}{c.admin ? ' (Admin)' : ''}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">No user has the Suppliers function in this company yet; ask an administrator.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDefineSupplier(null)}>OK</Button>
          </DialogActions>
        </ResponsiveDialog>
      )}

      <ResponsiveDialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="pc-del-title">
        <DialogTitle id="pc-del-title">Remove receipt {deleteIndex !== null ? deleteIndex + 1 : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{deleteIndex !== null ? `${lines[deleteIndex]?.supplierName || ''} · ${lines[deleteIndex]?.invoiceNo || ''} · ${formatAmount(lines[deleteIndex]?.total)} ${currency}` : ''}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteIndex(null)}>Keep</Button>
          <Button color="error" onClick={() => { if (deleteIndex !== null) void removeLine(deleteIndex); }}>Remove</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={retryOpen} onClose={isBusy ? undefined : () => setRetryOpen(false)} maxWidth="sm" fullWidth aria-labelledby="pc-retry-title">
        <DialogTitle id="pc-retry-title">Post again</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              PRPO posts what SAP lacks: {plural(toPost.length, 'receipt')} still to post. Receipts already in SAP are not posted twice, and an invoice already in SAP only gets its payment.
            </Typography>
            {toPost.length ? (
              <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
                {toPost.map((l) => (
                  <Box component="li" key={l.lineId ?? l.lineNum}>
                    <Typography variant="body2">
                      Receipt {l.lineNum} · {l.supplierName || '—'} · {formatAmount(l.total)} {currency}
                      {l.effectiveAccount ? ` · ${l.effectiveAccount}${l.effectiveAccountName ? ` ${l.effectiveAccountName}` : ''}` : ' · no account'}
                    </Typography>
                    {l.sapInvoiceNum ? <Typography variant="caption" color="text.secondary" display="block">A/P invoice {l.sapInvoiceNum} is in SAP: only its payment is left</Typography> : null}
                    {l.postingError ? <Typography variant="caption" color="error" display="block">Last refusal: {l.postingError}</Typography> : null}
                  </Box>
                ))}
              </Stack>
            ) : null}
            <TextField
              id="pc-retry-date"
              type="date"
              label="Posting date"
              value={retryDate}
              onChange={(e) => setRetryDate(cleanDateInput(e.target.value))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: claimPostingDate || undefined, max: todayIso() }}
              helperText={`For what is left${claimPostingDate ? `: from ${formatDate(claimPostingDate)}` : ''} up to today, e.g. when SAP's posting period is closed. Receipts already posted keep theirs.`}
              sx={{ maxWidth: 420 }}
            />
            {retryError ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{retryError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRetryOpen(false)} disabled={isBusy}>Back</Button>
          <Button onClick={() => { void retry(); }} disabled={isBusy}>{busy === 'retry' ? 'Posting…' : 'Post again'}</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!action} onClose={isBusy ? undefined : () => setAction(null)} maxWidth="sm" fullWidth aria-labelledby="pc-action-title">
        {action ? (
          <>
            <DialogTitle id="pc-action-title">{action === 'fin-approve' && !finStage.isLast ? 'Approve the claim' : ACTION_TEXT[action].title}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {action.startsWith('fin-') && stageText ? <Typography variant="body2" fontWeight={600}>{stageText}</Typography> : null}
                <Typography variant="body2">
                  {action === 'fin-approve'
                    ? (finStage.isLast ? finApproveHelp : `The claim moves to the next finance stage${finStage.nextName ? `: ${finStage.nextName}` : ''}.`)
                    : action === 'fin-revert'
                      ? `The claim goes back to ${finStage.previousName || 'the previous finance stage'}, whose approvers decide again.`
                      : ACTION_TEXT[action].help}
                </Typography>
                {action === 'fin-approve' && finStage.isLast ? (
                  <>
                    <TextField
                      id="pc-action-posting-date"
                      type="date"
                      label="Posting date"
                      value={postingDate}
                      onChange={(e) => setPostingDate(cleanDateInput(e.target.value))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: todayIso() }}
                      helperText={`SAP's posting date of the invoices and payments · total ${formatAmount(detail?.claim.total)} ${currency}`}
                      required
                      sx={{ maxWidth: 420 }}
                    />
                    {originalsMissing ? (
                      <Alert severity="warning">
                        The original receipts are not marked as received yet: they must be before this approval (Original receipts, on this page).
                      </Alert>
                    ) : null}
                  </>
                ) : action === 'fin-approve' ? (
                  <Typography variant="body2">Total {formatAmount(detail?.claim.total)} {currency}</Typography>
                ) : null}
                <TextField
                  id="pc-action-comment"
                  label={ACTION_TEXT[action].commentRequired ? 'Comment (required)' : 'Comment (optional)'}
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                  multiline
                  minRows={2}
                  fullWidth
                  required={ACTION_TEXT[action].commentRequired}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setAction(null)} disabled={isBusy}>Back</Button>
              <Button
                color={action === 'reject' || action === 'fin-reject' ? 'error' : 'primary'}
                onClick={() => { void runAction(); }}
                disabled={isBusy || (ACTION_TEXT[action].commentRequired && !comment.trim()) || (action === 'fin-approve' && approveDisabled)}
              >
                {isBusy ? 'Working…' : action === 'fin-approve' && !finStage.isLast ? 'Approve' : ACTION_TEXT[action].button}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </ResponsiveDialog>

      <ReimbursementDialog
        open={reimburseOpen}
        currency={currency}
        paidAmount={paidAmount}
        postInSap={process.reimbursePost}
        accounts={process}
        cashAccount={detail?.claim.sapAccount ?? null}
        auxiliary={detail?.claim.auxiliaryCode ?? null}
        onClose={() => setReimburseOpen(false)}
        onSave={recordReimbursement}
      />

      <OriginalsDialog
        open={originalsDialog.open}
        mode={originalsDialog.mode}
        onClose={() => setOriginalsDialog((o) => ({ ...o, open: false }))}
        onSave={saveOriginals}
      />
    </Box>
  );
};

export default PettyCashClaim;
